import { Fragment, ReactNode, useState } from 'react';

import { UserMessage } from '../../../../types/UserMessage';
import { UserStatus } from '../../../../types/UserStatus';
import { Button } from '../../../cdl/Button/Button';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { DeactivateMemberModal } from '../../components/DeactivateMemberModal';
import { useUserManagementUpdate } from '../../list/hooks/useUserManagementUpdate';
import { useUserInvitationResend } from '../hooks/useUserInvitationResend';

interface MemberDetailActionsProps {
    userId: string;
    user: UserMessage;
    onEditClick: () => void;
}

export const MemberDetailActions = ({ userId, user, onEditClick }: MemberDetailActionsProps) => {
    const role = useRole();
    const { addErrorToast, addToast } = useToasts();
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState<boolean>(false);

    const resendInvitationMutation = useUserInvitationResend();
    const userManagementUpdateMutation = useUserManagementUpdate();

    const resendInvite = () => {
        return resendInvitationMutation.mutate(userId, {
            onSuccess: () => addToast(translate('user.resendInvitationToasts.success')),
            onError: () => addErrorToast(translate('user.resendInvitationToasts.error')),
        });
    };

    const handleActivate = async () => {
        if (!user) return;

        try {
            await userManagementUpdateMutation.mutateAsync({
                userId: user.id,
                userMessage: {
                    ...user,
                    status: UserStatus.ACTIVE,
                },
            });
            addToast(translate('user.activateUserToasts.success'));
        } catch {
            addErrorToast(translate('user.activateUserToasts.error'));
        }
    };

    const buttons: ReactNode[] = [];
    const isSsoEnabled = user?.ssoEnabled;

    if (role.isSuperAdmin() && user?.status === UserStatus.INVITED) {
        buttons.push(<Button onClick={resendInvite}>{translate('user.resendInvitation')}</Button>);
    }

    if (role.hasAnyAdminRights() && role.user.id !== userId && !isSsoEnabled) {
        if (user?.status === UserStatus.ACTIVE) {
            buttons.push(
                <Button onClick={() => setIsDeactivateModalOpen(true)}>
                    {translate('user.deactivateMemberButton')}
                </Button>
            );
        }

        if (user?.status === UserStatus.INACTIVE) {
            buttons.push(<Button onClick={handleActivate}>{translate('user.activateMemberButton')}</Button>);
        }
    }

    if (role.user.id !== userId) {
        if (isSsoEnabled) {
            buttons.push(
                <Tooltip label={translate('user.ssoManagedTooltip')}>
                    <Button emphasis="high" disabled>
                        {translate('actions.edit')}
                    </Button>
                </Tooltip>
            );
        } else {
            buttons.push(
                <Button emphasis="high" onClick={onEditClick}>
                    {translate('actions.edit')}
                </Button>
            );
        }
    }

    return (
        <>
            <ButtonGroup>
                {buttons.map((button, index) => (
                    <Fragment key={index}>{button}</Fragment>
                ))}
            </ButtonGroup>

            <DeactivateMemberModal
                isOpen={isDeactivateModalOpen}
                onDismiss={() => setIsDeactivateModalOpen(false)}
                user={user}
            />
        </>
    );
};
